import { useState, useEffect, useCallback } from 'react';
import { Upload, message } from 'antd';
import { MAX_FILE_SIZE } from 'constants/index';
import { useAttachmentTypeQuery } from 'api';

export interface AttachmentItem {
  type: string;
  file: File;
}

export const useAttachments = (entityName: string = 'PURCHASE_REQUEST') => {
  const [attachmentType, setAttachmentType] = useState<string | undefined>();
  const [fileList, setFileList] = useState<File[]>([]);
  const [attachments, setAttachments] = useState<AttachmentItem[]>([]);
  const [error, setError] = useState('');
  const [disabledActions, setDisabledActions] = useState(true);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [isPreviewVisible, setIsPreviewVisible] = useState(false);

  const { data: attachmentTypes = [] } = useAttachmentTypeQuery({ entityName });

  useEffect(() => {
    setDisabledActions(!fileList?.length || !attachmentType);
  }, [fileList, attachmentType]);

  const handleAddAttachment = useCallback(() => {
    if (fileList.length > 0 && attachmentType) {
      setAttachments((prev) => [...prev, { type: attachmentType, file: fileList[0] }]);
      setAttachmentType(undefined);
      setFileList([]);
    } else {
      message.warning('Please select type and upload a file.');
    }
  }, [fileList, attachmentType]);

  const handleDeleteAttachment = useCallback((index: number) => {
    setAttachments((prev) => {
      const newAttachments = [...prev];
      newAttachments.splice(index, 1);
      return newAttachments;
    });
  }, []);

  const handleFileSizeValidation = useCallback((file: File) => {
    if (file.size > MAX_FILE_SIZE) {
      setFileList([]);
      setError('File exceeds the maximum size of 5 MB.');
      return Upload.LIST_IGNORE;
    }

    const isImage = ['image/jpeg', 'image/png', 'image/jpg'].includes(file.type);
    if (isImage) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setPreviewImage(e.target?.result as string);
        setIsPreviewVisible(true);
      };
      reader.readAsDataURL(file);
    }

    setFileList([file]);
    setError('');
    return false;
  }, []);

  const handleCancelAttachment = useCallback(() => {
    setAttachmentType(undefined);
    setFileList([]);
  }, []);

  return {
    attachmentType,
    fileList,
    attachments,
    error,
    disabledActions,
    previewImage,
    isPreviewVisible,
    attachmentTypes,
    setAttachmentType,
    setFileList,
    setIsPreviewVisible,
    handleAddAttachment,
    handleDeleteAttachment,
    handleFileSizeValidation,
    handleCancelAttachment,
  };
};
